import { useState } from "react";
import { Users, Building2, Briefcase, ShieldCheck, AlertTriangle, DollarSign, ChevronRight, Check, Ban, RefreshCw, Download, TrendingUp, Clock } from "lucide-react";
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useAdminData } from "../../hooks/useAdminData";
import { talents as talentsApi, postings as postingsApi } from "../../api/endpoints";
import { downloadCsv } from "../../exportCsv";
import { AddToast } from "../../components/feedback/Toast";
import { Avatar } from "../../components/ui/Avatar";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { KpiCard } from "../../components/ui/KpiCard";
import { PageHeader } from "../../components/ui/PageHeader";
import { Dot, StatusBadge } from "../../components/ui/StatusBadge";
import { statusColors } from "../../theme/status";
import { CORAL, F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";
import { describeChange } from "../../utils/describeChange";

// ── Dashboard ─────────────────────────────────────────────────────────────────

export function DashboardView({ onNav, addToast }: { onNav: (id: string) => void; addToast: AddToast }) {
  const { ADMIN_REPORTS, VERIFICATION_QUEUE, ADMIN_POSTINGS, AUDIT_LOG, PLATFORM_STATS, MONTHLY_SIGNUPS, CATEGORY_DISTRIBUTION, ADMIN_TALENTS, refetchTalents } = useAdminData();
  const openReports = ADMIN_REPORTS.filter((r) => r.status === "Open" || r.status === "Under Review");
  const pendingVerif = VERIFICATION_QUEUE.length;
  const pendingPostings = ADMIN_POSTINGS.filter((p) => p.status === "Pending Review");
  const recentActivity = AUDIT_LOG.slice(0, 6);
  const [accountStates, setAccountStates] = useState<Record<string, string>>({});

  function exportReport() {
    const rows = Object.entries(PLATFORM_STATS);
    downloadCsv("donatella-platform-report", rows, [
      { header: "Metric", value: ([key]) => key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()) },
      { header: "Value", value: ([, value]) => value as string | number },
    ]);
    addToast(`Exported ${rows.length} metrics`, "success");
  }

  function approvePosting(id: string, title: string) {
    postingsApi.update(id, { status: "Active" })
      .then(() => addToast(`"${title}" approved`, "success"))
      .catch((e) => addToast(e instanceof Error ? e.message : "Failed to approve posting", "error"));
  }

  function getAccountState(id: string, defaultState: string) {
    return accountStates[id] ?? defaultState;
  }
  function toggleAccount(id: string, name: string, currentState: string) {
    const next = currentState === "Active" ? "Suspended" : "Active";
    setAccountStates((p) => ({ ...p, [id]: next }));
    talentsApi.update(id, { account_status: next }).then(refetchTalents).catch(() => { });
    addToast(`${name} ${next === "Suspended" ? "suspended" : "reactivated"}`, next === "Suspended" ? "error" : "success");
  }

  return (
    <div>
      <PageHeader title="Dashboard" sub="Platform overview as of 9 Sep 2026" actions={
        <Btn variant="ghost" onClick={exportReport} iconOnlyPhone><Download size={13} /> Export report</Btn>
      } />

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 150px), 1fr))", gap: "14px", marginBottom: "28px" }}>
        <KpiCard icon={<Users size={16} />} label="Total talent" value={PLATFORM_STATS.totalTalents.toLocaleString()} sub={`+${PLATFORM_STATS.newTalents30d} this month`} />
        <KpiCard icon={<Building2 size={16} />} label="Total organisations" value={PLATFORM_STATS.totalOrgs.toLocaleString()} sub={`+${PLATFORM_STATS.newOrgs30d} this month`} />
        <KpiCard icon={<Briefcase size={16} />} label="Active postings" value={PLATFORM_STATS.activePostings.toLocaleString()} />
        <KpiCard icon={<DollarSign size={16} />} label="Payments volume" value={PLATFORM_STATS.totalPaymentsVolume} sub="All time" />
        <KpiCard icon={<TrendingUp size={16} />} label="Hire rate" value={PLATFORM_STATS.hireRate} sub={`Avg ${PLATFORM_STATS.avgDaysToHire}d to hire`} />
        <KpiCard icon={<ShieldCheck size={16} />} label="Verification pending" value={PLATFORM_STATS.verificationPending.toLocaleString()} />
      </div>

      {/* Charts row */}
      <div className="rg-split" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "14px", marginBottom: "28px" }}>
        <Card style={{ padding: "20px 22px" }}>
          <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F, marginBottom: "16px" }}>Platform growth — signups</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={MONTHLY_SIGNUPS} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: T.muted, fontFamily: F }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: T.muted, fontFamily: F }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontFamily: F, fontSize: "11px", borderRadius: "8px", border: `1px solid ${T.border}` }} />
              <Area type="monotone" dataKey="talent" stroke={T.text} fill="rgba(10,10,10,0.06)" strokeWidth={1.5} name="Talent" />
              <Area type="monotone" dataKey="orgs" stroke={CORAL} fill="rgba(209,0,75,0.06)" strokeWidth={1.5} name="Orgs" />
              <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: "10px", fontFamily: F }} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card style={{ padding: "20px 22px" }}>
          <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F, marginBottom: "16px" }}>Talent by category</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={CATEGORY_DISTRIBUTION} cx="50%" cy="50%" outerRadius={75} dataKey="value" nameKey="name" strokeWidth={0}>
                {CATEGORY_DISTRIBUTION.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? T.text : i === 1 ? CORAL : `hsl(${220 + i * 28},${30 + i * 4}%,${40 + i * 5}%)`} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ fontFamily: F, fontSize: "11px", borderRadius: "8px", border: `1px solid ${T.border}` }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Attention queues */}
      <div className="rg-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px", marginBottom: "28px" }}>
        {/* Verification queue */}
        <Card style={{ padding: "18px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <ShieldCheck size={14} style={{ color: T.sub }} />
              <p style={{ fontSize: "12px", fontWeight: 600, color: T.text, fontFamily: F }}>Verification queue</p>
            </div>
            <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: "9999px", fontSize: "10px", fontWeight: 700, padding: "2px 8px", fontFamily: F }}>{pendingVerif}</span>
          </div>
          {VERIFICATION_QUEUE.slice(0, 4).map((v) => (
            <div key={v.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
              <div>
                <p style={{ fontSize: "12px", fontWeight: 500, color: T.text, fontFamily: F }}>{v.entityName}</p>
                <p style={{ fontSize: "10px", color: T.sub, fontFamily: F }}>{v.entityType} · {v.submittedDate}</p>
              </div>
              <StatusBadge status={v.priority} />
            </div>
          ))}
          <button onClick={() => onNav("verification")} style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "12px", fontSize: "11px", color: CORAL, fontFamily: F, fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            View all <ChevronRight size={11} />
          </button>
        </Card>

        {/* Open reports */}
        <Card style={{ padding: "18px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <AlertTriangle size={14} style={{ color: T.sub }} />
              <p style={{ fontSize: "12px", fontWeight: 600, color: T.text, fontFamily: F }}>Open reports</p>
            </div>
            <span style={{ background: "#fee2e2", color: "#991b1b", borderRadius: "9999px", fontSize: "10px", fontWeight: 700, padding: "2px 8px", fontFamily: F }}>{openReports.length}</span>
          </div>
          {openReports.slice(0, 4).map((r) => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
              <div>
                <p style={{ fontSize: "12px", fontWeight: 500, color: T.text, fontFamily: F }}>{r.reportedEntityName}</p>
                <p style={{ fontSize: "10px", color: T.sub, fontFamily: F }}>{r.reason}</p>
              </div>
              <StatusBadge status={r.severity} />
            </div>
          ))}
          <button onClick={() => onNav("moderation")} style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "12px", fontSize: "11px", color: CORAL, fontFamily: F, fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            Review now <ChevronRight size={11} />
          </button>
        </Card>

        {/* Pending postings */}
        <Card style={{ padding: "18px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <Clock size={14} style={{ color: T.sub }} />
              <p style={{ fontSize: "12px", fontWeight: 600, color: T.text, fontFamily: F }}>Pending review</p>
            </div>
            <span style={{ background: "#dbeafe", color: "#1e40af", borderRadius: "9999px", fontSize: "10px", fontWeight: 700, padding: "2px 8px", fontFamily: F }}>{pendingPostings.length}</span>
          </div>
          {pendingPostings.map((p) => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
              <div>
                <p style={{ fontSize: "12px", fontWeight: 500, color: T.text, fontFamily: F }}>{p.title}</p>
                <p style={{ fontSize: "10px", color: T.sub, fontFamily: F }}>{p.orgName}</p>
              </div>
              <Btn variant="ghost" size="xs" onClick={() => approvePosting(p.id, p.title)}><Check size={10} /></Btn>
            </div>
          ))}
          {pendingPostings.length === 0 && (
            <p style={{ padding: "8px 0", fontSize: "11px", color: T.muted, fontFamily: F }}>Nothing waiting for review.</p>
          )}
          <button onClick={() => onNav("postings")} style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "12px", fontSize: "11px", color: CORAL, fontFamily: F, fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            View postings <ChevronRight size={11} />
          </button>
        </Card>
      </div>

      {/* Account quick-management */}
      <Card style={{ padding: "20px 22px", marginBottom: "14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
          <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F }}>Recent accounts</p>
          <div style={{ display: "flex", gap: "6px" }}>
            <button onClick={() => onNav("talents")} style={{ fontSize: "11px", color: CORAL, fontFamily: F, fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>View all →</button>
          </div>
        </div>
        <TableWrap>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F, minWidth: "600px" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Account", "Type", "Category", "Status", "Action"].map((h) => (
                  <th key={h} style={{ padding: "7px 12px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ADMIN_TALENTS.slice(0, 5).map((t) => {
                const state = getAccountState(t.id, t.account);
                return (
                  <tr key={t.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: "9px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Avatar initials={t.initials} color={t.color} size={26} />
                        <p style={{ fontSize: "12px", fontWeight: 600, color: T.text }}>{t.name}</p>
                      </div>
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: "11px", color: T.sub }}>Talent</td>
                    <td style={{ padding: "9px 12px", fontSize: "11px", color: T.sub }}>{t.category}</td>
                    <td style={{ padding: "9px 12px" }}><StatusBadge status={state} /></td>
                    <td style={{ padding: "9px 12px" }}>
                      {state === "Active"
                        ? <Btn variant="ghost" size="xs" onClick={() => toggleAccount(t.id, t.name, state)}><Ban size={10} /> Suspend</Btn>
                        : <Btn variant="ghost" size="xs" onClick={() => toggleAccount(t.id, t.name, state)} style={{ color: "#166534", background: "#e4f5ec", border: "1px solid #bbf7d0" }}><RefreshCw size={10} /> Reactivate</Btn>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      </Card>

      {/* Recent admin activity */}
      <Card style={{ padding: "20px 22px" }}>
        <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F, marginBottom: "14px" }}>Recent admin activity</p>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {recentActivity.map((ev, i) => {
            const change = describeChange(ev.previousState, ev.newState);
            return (
              <div key={ev.id} className="activity-row" style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "10px 0", borderBottom: i < recentActivity.length - 1 ? `1px solid ${T.border}` : "none" }}>
                <Dot color={ev.result === "Success" ? statusColors.Success.fg : statusColors.Failed.fg} style={{ marginTop: 5 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: "12px", color: T.text, fontFamily: F, overflowWrap: "anywhere" }}><span style={{ fontWeight: 600 }}>{ev.admin}</span> · {ev.action} · <span style={{ color: T.sub }}>{ev.entityName}</span></p>
                  {change && <p style={{ fontSize: "10px", color: T.muted, fontFamily: F, marginTop: "2px", overflowWrap: "anywhere", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }} title={change}>{change}</p>}
                </div>
                <p className="activity-time" style={{ fontSize: "10px", color: T.muted, fontFamily: F, flexShrink: 0, whiteSpace: "nowrap" }}>{ev.timestamp}</p>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
